/**
 * Index-DB holder markers (TRA-304).
 *
 * TRA-38 lets two checkouts of the same git remote share one on-disk index DB.
 * That is a win when the checkouts are used *sequentially* (Multica's per-run
 * ephemeral clones) and a correctness problem when they are used
 * *concurrently* — both watchers write into the same DB and the last indexer
 * wins for shared file paths, silently.
 *
 * A holder marker is the smallest thing that tells those two cases apart: the
 * process that opens a project's DB writes `<dbPath>.holders/<id>.json`, and
 * `registerProject()` refuses to reuse a sibling's dbPath while a holder for a
 * *different* root is still live. No lease renewal, no lock file, no daemon
 * coordination — a holder is just "this pid claimed this DB from this root".
 *
 * A holder is live iff its pid still exists AND it was written within
 * {@link HOLDER_TTL_MS}. The TTL matters: after a reboot the OS recycles pids,
 * so a stale marker whose pid got reused would pin a dead holder forever.
 */

import crypto from 'node:crypto';
import fs from 'node:fs';
import path from 'node:path';

/** A holder older than this is dead regardless of whether its pid resolves. */
export const HOLDER_TTL_MS = 24 * 60 * 60 * 1000;

interface HolderFile {
  pid: number;
  root: string;
  startedAt: string;
}

/** Directory holding the markers for one index DB. Sits beside the DB file. */
export function holdersDir(dbPath: string): string {
  return `${dbPath}.holders`;
}

function holderId(root: string): string {
  return crypto.createHash('sha256').update(path.resolve(root)).digest('hex').slice(0, 16);
}

function isLive(holder: HolderFile): boolean {
  const startedAt = Date.parse(holder.startedAt);
  if (!Number.isFinite(startedAt) || Date.now() - startedAt > HOLDER_TTL_MS) return false;
  try {
    process.kill(holder.pid, 0);
    return true;
  } catch (err) {
    // EPERM means the pid exists but belongs to another user — still live.
    return (err as NodeJS.ErrnoException).code === 'EPERM';
  }
}

/**
 * Roots that currently hold `dbPath`, excluding `excludeRoot`. Dead markers are
 * unlinked opportunistically as they are scanned.
 *
 * Throws if the holder directory exists but cannot be read. Callers decide what
 * an unreadable directory means for them; every call site here fails toward
 * "assume in use", never toward silent sharing or deletion.
 */
export function liveHolderRoots(dbPath: string, excludeRoot?: string): string[] {
  const dir = holdersDir(dbPath);
  let files: string[];
  try {
    files = fs.readdirSync(dir);
  } catch (err) {
    if ((err as NodeJS.ErrnoException).code === 'ENOENT') return [];
    throw err;
  }

  const exclude = excludeRoot ? path.resolve(excludeRoot) : null;
  const roots: string[] = [];
  for (const file of files) {
    if (!file.endsWith('.json')) continue;
    const full = path.join(dir, file);
    let holder: HolderFile | null = null;
    try {
      holder = JSON.parse(fs.readFileSync(full, 'utf-8')) as HolderFile;
    } catch {
      /* unreadable or malformed marker — reaped below */
    }
    const usable = !!holder && typeof holder.pid === 'number' && typeof holder.root === 'string';
    if (!usable || !isLive(holder as HolderFile)) {
      try {
        fs.unlinkSync(full);
      } catch {
        /* best effort */
      }
      continue;
    }
    const root = path.resolve((holder as HolderFile).root);
    if (exclude && root === exclude) continue;
    roots.push(root);
  }
  return roots;
}

/** True if any root other than `excludeRoot` currently holds `dbPath`. */
export function hasLiveHolder(dbPath: string, excludeRoot?: string): boolean {
  return liveHolderRoots(dbPath, excludeRoot).length > 0;
}

/**
 * Same as {@link hasLiveHolder}, but an unreadable holder directory answers
 * `true`. For deletion paths (prune, removeProjectArtifacts) "I cannot tell"
 * has to mean "keep it".
 */
export function hasLiveHolderOrUnknown(dbPath: string, excludeRoot?: string): boolean {
  try {
    return hasLiveHolder(dbPath, excludeRoot);
  } catch {
    return true;
  }
}

const announced = new Map<string, { dbPath: string; root: string }>();
let exitHookInstalled = false;

function holderKey(dbPath: string, root: string): string {
  return `${dbPath} ${root}`;
}

function removeHolderFile(dbPath: string, root: string): void {
  try {
    fs.unlinkSync(path.join(holdersDir(dbPath), `${holderId(root)}.json`));
  } catch {
    /* best effort — a leftover marker is reaped by the next scan anyway */
  }
}

/** Write this process's holder marker for (dbPath, root). Throws on fs error. */
export function announceDbHolder(dbPath: string, root: string): void {
  const absRoot = path.resolve(root);
  const dir = holdersDir(dbPath);
  fs.mkdirSync(dir, { recursive: true });
  const holder: HolderFile = {
    pid: process.pid,
    root: absRoot,
    startedAt: new Date().toISOString(),
  };
  fs.writeFileSync(path.join(dir, `${holderId(absRoot)}.json`), JSON.stringify(holder));
  announced.set(holderKey(dbPath, absRoot), { dbPath, root: absRoot });

  if (!exitHookInstalled) {
    exitHookInstalled = true;
    process.on('exit', () => {
      for (const h of announced.values()) removeHolderFile(h.dbPath, h.root);
      announced.clear();
    });
  }
}

/** Drop this process's holder marker for (dbPath, root). Best effort. */
export function releaseDbHolder(dbPath: string, root: string): void {
  const absRoot = path.resolve(root);
  announced.delete(holderKey(dbPath, absRoot));
  removeHolderFile(dbPath, absRoot);
}

/**
 * Drop every marker this process announced for `root`, whichever DB it points
 * at. A daemon can announce one root against more than one dbPath over its life
 * (registration resolves one; the project it then opens resolves another for
 * git worktrees), and stopping the project must not leave any of them behind.
 */
export function releaseDbHoldersForRoot(root: string): void {
  const absRoot = path.resolve(root);
  for (const h of [...announced.values()]) {
    if (h.root === absRoot) releaseDbHolder(h.dbPath, h.root);
  }
}

/** Delete a DB's whole holder directory. Used when the DB itself is deleted. */
export function removeHoldersDir(dbPath: string): void {
  try {
    fs.rmSync(holdersDir(dbPath), { recursive: true, force: true });
  } catch {
    /* best effort */
  }
}
